// src/components/common/NearestStationsPanel.js
import React, { useState, useEffect } from 'react';
import {
  MapPin, Navigation, Clock, AlertCircle, RefreshCw,
  CheckCircle, XCircle,
  Car, Bike, User
} from 'lucide-react';

const NearestStationsPanel = ({ stations, onStationSelect }) => {
  const [userLocation, setUserLocation] = useState(null);
  const [sortedStations, setSortedStations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [travelMode, setTravelMode] = useState('driving');
  const [locationPermission, setLocationPermission] = useState('prompt');

  useEffect(() => {
    checkLocationPermission();
  }, []);

  const checkLocationPermission = async () => {
    if ('permissions' in navigator) {
      try {
        const result = await navigator.permissions.query({ name: 'geolocation' });
        setLocationPermission(result.state);
        
        result.addEventListener('change', () => {
          setLocationPermission(result.state);
        });
      } catch (error) {
        console.error('Error checking location permission:', error);
      }
    }
  };

  const getPosition = (highAccuracy) =>
    new Promise((resolve, reject) =>
      navigator.geolocation.getCurrentPosition(resolve, reject, {
        enableHighAccuracy: highAccuracy,
        timeout: highAccuracy ? 12000 : 8000,
        maximumAge: 60000,
      })
    );

  const requestLocation = async () => {
    setLoading(true);
    setError(null);

    try {
      let position;
      try {
        // First attempt: high-accuracy GPS
        position = await getPosition(true);
      } catch (firstErr) {
        // GPS failed (unavailable or timed out) — fall back to network/IP location
        if (firstErr.code === 1) throw firstErr; // genuinely denied — don't retry
        position = await getPosition(false);
      }

      const userLoc = {
        lat: position.coords.latitude,
        lng: position.coords.longitude,
        accuracy: position.coords.accuracy,
      };

      setUserLocation(userLoc);
      calculateDistances(userLoc);
      setLocationPermission('granted');
    } catch (err) {
      if (err.code === 1) {
        // Permission denied — could be browser or OS level
        setLocationPermission('denied');
        setError('denied');
      } else {
        setError('unavailable');
      }
    } finally {
      setLoading(false);
    }
  };

  const calculateDistances = (userLoc) => {
    if (!stations || stations.length === 0) return;

    const stationsWithDistance = stations.map(station => {
      const distance = calculateDistance(
        userLoc.lat,
        userLoc.lng,
        station.lat,
        station.lng
      );

      return {
        ...station,
        distance,
        distanceText: formatDistance(distance),
        travelTime: estimateTravelTime(distance, travelMode)
      };
    });

    const sorted = stationsWithDistance.sort((a, b) => a.distance - b.distance);
    setSortedStations(sorted);
  };

  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371;
    const dLat = toRadians(lat2 - lat1);
    const dLon = toRadians(lon2 - lon1);
    
    const a = 
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(toRadians(lat1)) * Math.cos(toRadians(lat2)) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  };

  const toRadians = (degrees) => degrees * (Math.PI / 180);

  const formatDistance = (distanceKm) => {
    if (distanceKm < 1) {
      return `${Math.round(distanceKm * 1000)} m`;
    } else if (distanceKm < 10) {
      return `${distanceKm.toFixed(1)} km`;
    } else {
      return `${Math.round(distanceKm)} km`;
    }
  };

  const estimateTravelTime = (distanceKm, mode) => {
    const speeds = {
      walking: 5,
      cycling: 15,
      driving: 50
    };

    const speed = speeds[mode] || speeds.driving;
    const hours = distanceKm / speed;
    const minutes = Math.round(hours * 60);

    if (minutes < 60) {
      return `${minutes} min`;
    } else {
      const hrs = Math.floor(minutes / 60);
      const mins = minutes % 60;
      return mins > 0 ? `${hrs}h ${mins}m` : `${hrs}h`;
    }
  };

  useEffect(() => {
    if (userLocation) {
      calculateDistances(userLocation);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [travelMode]);

  const getTravelIcon = () => {
    switch (travelMode) {
      case 'walking': return <User className="w-4 h-4" />;
      case 'cycling': return <Bike className="w-4 h-4" />;
      case 'driving': return <Car className="w-4 h-4" />;
      default: return <Car className="w-4 h-4" />;
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <Navigation className="w-6 h-6 text-blue-600" />
          <div>
            <h2 className="text-xl font-bold text-gray-900">Nearest Stations</h2>
            <p className="text-sm text-gray-600">Find weather stations near you</p>
          </div>
        </div>
        {userLocation && (
          <button
            onClick={requestLocation}
            disabled={loading}
            className="px-3 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors flex items-center space-x-2"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            <span>Update</span>
          </button>
        )}
      </div>

      {!userLocation ? (
        <div className="text-center py-8">
          <MapPin className="w-16 h-16 text-gray-300 mx-auto mb-4" />

          {error === 'denied' ? (
            <div className="space-y-3 text-left bg-red-50 border border-red-200 rounded-xl p-4 mb-4 max-w-sm mx-auto">
              <div className="flex items-center space-x-2">
                <XCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
                <p className="text-sm font-semibold text-red-800">Location access was blocked</p>
              </div>
              <p className="text-xs text-red-700 font-medium">To fix this:</p>
              <ul className="text-xs text-red-700 space-y-1 list-disc list-inside">
                <li><strong>Android Chrome:</strong> tap the lock icon in the address bar → Site settings → Location → Allow</li>
                <li><strong>Android Firefox:</strong> tap the shield icon → Permissions → Location → Allow</li>
                <li><strong>System:</strong> Settings → Apps → Chrome/Firefox → Permissions → Location → Allow</li>
              </ul>
              <p className="text-xs text-red-600">After allowing, tap <strong>Retry</strong> below.</p>
            </div>
          ) : error === 'unavailable' ? (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4 max-w-sm mx-auto text-left">
              <div className="flex items-center space-x-2 mb-1">
                <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0" />
                <p className="text-sm font-semibold text-amber-800">Could not determine your location</p>
              </div>
              <p className="text-xs text-amber-700">Make sure Location Services are on in your device settings, then tap Retry.</p>
            </div>
          ) : (
            <div className="mb-4">
              <p className="text-gray-700 font-medium mb-1">Find Stations Near You</p>
              <p className="text-sm text-gray-500 max-w-xs mx-auto">Allow location access to sort stations by distance from you.</p>
            </div>
          )}

          <button
            onClick={requestLocation}
            disabled={loading}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium flex items-center space-x-2 mx-auto disabled:opacity-60"
          >
            {loading ? (
              <>
                <RefreshCw className="w-5 h-5 animate-spin" />
                <span>Detecting location…</span>
              </>
            ) : (
              <>
                <Navigation className="w-5 h-5" />
                <span>{error ? 'Retry' : 'Enable Location'}</span>
              </>
            )}
          </button>
        </div>
      ) : (
        <>
          {/* Current Location Info */}
          {(() => {
            const acc = Math.round(userLocation.accuracy || 0);
            const poor = acc > 5000;
            const moderate = acc > 1000 && acc <= 5000;
            return (
              <div className={`border rounded-lg p-4 mb-4 ${poor ? 'bg-amber-50 border-amber-300' : moderate ? 'bg-yellow-50 border-yellow-200' : 'bg-green-50 border-green-200'}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    {poor ? <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0" /> : <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />}
                    <div>
                      <p className={`text-sm font-medium ${poor ? 'text-amber-900' : 'text-green-900'}`}>Your Location</p>
                      <p className={`text-xs ${poor ? 'text-amber-700' : 'text-green-700'}`}>
                        {userLocation.lat.toFixed(4)}°, {userLocation.lng.toFixed(4)}°
                      </p>
                    </div>
                  </div>
                  <div className={`text-xs font-semibold ${poor ? 'text-amber-700' : moderate ? 'text-yellow-700' : 'text-green-700'}`}>
                    ±{acc >= 1000 ? `${(acc / 1000).toFixed(0)} km` : `${acc} m`}
                  </div>
                </div>
                {poor && (
                  <div className="mt-3 pt-3 border-t border-amber-200">
                    <div className="flex items-center gap-1.5 text-xs text-amber-800 font-semibold mb-1">
                      <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
                      Low GPS accuracy — distances may be inaccurate
                    </div>
                    <p className="text-xs text-amber-700">Your device is using network/IP location instead of GPS. For precise results:</p>
                    <ul className="text-xs text-amber-700 mt-1 space-y-0.5 list-disc list-inside">
                      <li>Open this app on your <strong>mobile phone</strong> with GPS enabled</li>
                      <li>On desktop: enable <strong>Location Services</strong> in OS settings</li>
                      <li>Make sure Wi-Fi is on (helps browser locate you)</li>
                    </ul>
                    <button onClick={requestLocation} className="mt-2 text-xs font-semibold text-amber-900 underline">Retry location</button>
                  </div>
                )}
              </div>
            );
          })()}

          {/* Travel Mode Selector */}
          <div className="flex items-center space-x-2 mb-4">
            <span className="text-sm text-gray-600">Travel by:</span>
            <div className="flex space-x-2">
              {['walking', 'cycling', 'driving'].map((mode) => (
                <button
                  key={mode}
                  onClick={() => setTravelMode(mode)}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors flex items-center space-x-2 ${
                    travelMode === mode
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {mode === 'walking' && <User className="w-4 h-4" />}
                  {mode === 'cycling' && <Bike className="w-4 h-4" />}
                  {mode === 'driving' && <Car className="w-4 h-4" />}
                  <span className="capitalize">{mode}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Stations List */}
          <div className="space-y-3 max-h-96 overflow-y-auto">
            {sortedStations.map((station, index) => (
              <div
                key={station.id}
                className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => onStationSelect && onStationSelect(station.stationId || station.id, station)}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-3 flex-1">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                      index === 0 ? 'bg-blue-100 text-blue-700' :
                      index === 1 ? 'bg-blue-100 text-blue-700' :
                      index === 2 ? 'bg-orange-100 text-orange-700' :
                      'bg-gray-100 text-gray-700'
                    }`}>
                      {index + 1}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-1">
                        <h3 className="font-semibold text-gray-900">{station.name || station.location}</h3>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          station.status === 'online' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                        }`}>
                          {station.status?.toUpperCase()}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 mb-2">{station.location}</p>
                      
                      <div className="flex items-center space-x-4 text-sm">
                        <div className="flex items-center space-x-1 text-blue-600">
                          <MapPin className="w-4 h-4" />
                          <span className="font-medium">{station.distanceText}</span>
                        </div>
                        <div className="flex items-center space-x-1 text-orange-600">
                          <Clock className="w-4 h-4" />
                          <span>{station.travelTime}</span>
                        </div>
                        {getTravelIcon()}
                      </div>
                    </div>
                  </div>
                  
                  <button
                    className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      onStationSelect && onStationSelect(station.stationId || station.id, station);
                    }}
                  >
                    View
                  </button>
                </div>
              </div>
            ))}
          </div>

          {sortedStations.length > 1 && (
            <p className="text-center text-sm text-gray-500 mt-4">
              {sortedStations.length} station{sortedStations.length !== 1 ? 's' : ''} assigned to your account
            </p>
          )}
        </>
      )}
    </div>
  );
};

export default NearestStationsPanel;